import React, { useState, useRef, useEffect } from 'react'
import { IconButton, Checkbox, FormControlLabel, Box } from '@mui/material'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import DOMPurify from 'dompurify'
import { sanitizeForLog } from '../../constants/index'

const TermsAndCondition = ({ data, setIsChecked, isChecked }) => {
  const [step, setStep] = useState(0)
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false)
  const contentRef = useRef(null)

  const currentItem = data[step]
  const isLastStep = step === data.length - 1

  const handleNext = () => {
    setStep(prev => (prev === data.length - 1 ? 0 : prev + 1))
  }

  // Scroll to top every time step changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0
    }
    setHasScrolledToEnd(false) // reset when step changes
  }, [step])

  // Detect scroll to end
  const handleScroll = e => {
    const { scrollTop, scrollHeight, clientHeight } = e.target
    if (scrollTop + clientHeight >= scrollHeight - 5) {
      setHasScrolledToEnd(true)
    }
  }

  // Decode Base64 safely with proper UTF-8 handling
  const decodeAndSanitize = str => {
    try {
      // atob returns Latin-1 string; convert to proper UTF-8
      const binaryStr = atob(str)
      const bytes = Uint8Array.from(binaryStr, char => char.codePointAt(0))
      const decoded = new TextDecoder('utf-8').decode(bytes)

      // Sanitize decoded HTML and allow target attribute on links
      const sanitized = DOMPurify.sanitize(decoded, {
        USE_PROFILES: { html: true },
        ADD_ATTR: ['target', 'rel'],
      })

      // Add target="_blank", rel="noopener noreferrer", and blue color to all links
      const div = document.createElement('div')
      div.innerHTML = sanitized
      div.querySelectorAll('a').forEach(anchor => {
        anchor.setAttribute('target', '_blank')
        anchor.setAttribute('rel', 'noopener noreferrer')
        anchor.style.color = 'blue'
        anchor.style.textDecoration = 'underline'
      })
      return div.innerHTML
    } catch (err) {
      console.error('Base64 decode or sanitize error:', sanitizeForLog(err?.message || 'Unknown error'))
      return ''
    }
  }


  const decodedHtml = decodeAndSanitize(currentItem?.conditionString || '')

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Next button */}
      <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', mb: 2 }}>
        <IconButton
          onClick={handleNext}
          sx={{ color: '#666', '&:hover': { backgroundColor: '#e3f2fd' } }}
        >
          <ArrowDownwardIcon />
        </IconButton>
      </Box>

      {/* Scrollable content */}
      <Box
        ref={contentRef}
        sx={{ flex: 1, overflowY: 'auto', lineHeight: 1.6, p: 1 }}
        dangerouslySetInnerHTML={{ __html: decodedHtml }}
        onScroll={handleScroll}
      />

      {/* Checkbox only on last step *and* after scrolling to bottom */}
      {isLastStep && hasScrolledToEnd && (
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isChecked}
                onChange={() => setIsChecked(!isChecked)}
                 sx={{
                          '&.Mui-checked': {
                            color: 'var(--forbrand-color)',
                          },
                          '&.MuiCheckbox-indeterminate': {
                            color: 'var(--forbrand-secondary)',
                          },
                          color: 'var(--forbrand-secondary)',
                        }}
              />
            }
            label="I agree"
          />
        </Box>
      )}
    </Box>
  )
}

export default TermsAndCondition
